import Highlight from '../Highlight';
import HighlightCarousel from '../carousel/HighlightCarousel';
import chExpandedTpl from 'templates/carousels/highlightCarousel/expandingContainer.tpl';
import Breakpoints from 'helpers/breakpoints';
import MediaManager from 'components/highlight/MediaManager';
import { calculatePosition, dispatchEventToAllCarousels } from '../helpers';
import { trackAction } from 'helpers/trackingOM';
import WatchlistCta from 'components/watchlist/WatchlistCta';
import PreferencesCta from 'components/preferences/PreferencesCta';
import { getLocale } from 'services/LocalizationService';

const TRACK_SEARCH_SELECT = 'trackSearchResult';

export default class SearchContentHighlight extends Highlight(Object) {
    // desgin spec is 150% of 16:9 thumbnail, to get size of thumbnail we calculated the ratio thumbnail:window using KW carousel at each breakpoint
    // then to calculate we multiply window width by this number to get size of 16:9 thumbnail
    static thumbnailToWindowRatio = new Map([
        ['desktopWide', 0.144],
        ['desktop', 0.167],
        ['tabletLandscape', 0.22],
        ['tabletPortrait', 0.28],
    ]);

    bp = new Breakpoints();

    constructor(element, options, displayId = '') {
        super(element, options);
        this.type = 'ch';
        this.template = chExpandedTpl;
        this.cache = {};
        this.kw = {};
        this.displayId = displayId;
        this.searchUXImprovements = CBS?.Registry?.Features?.global_search_ux_improvements;
        this.initKw();
    }

    initKw() {
        const url = '/highlight/kw';
        this._http.doGet(url).then((res) => {
            if (!res.success) return;
            res = res.data.data;
            for (const item of res) {
                const id = item.id;
                this.kw[id] = item;
            }
        });
    }

    initResults(elements) {
        for (const element of elements) {
            const aalink = element.getAttribute('aa-link').split('|');
            const contentType = aalink[10];
            const contentLocked = aalink[23] === '1' ? true : false;
            const href = element.getAttribute('href'); // CH non cta clicks should lead to detail page
            const upsellUrl = element.getAttribute('ch-link'); // CH non cta clicks should lead to detail page
            this._http.regionBaseUrl = '';

            let id;

            let extraData = {
                isSearch: true,
                locked: contentLocked,
                href,
                upsellUrl,
            };

            // badge label check
            const badge = element.querySelectorAll('.new-item');

            if (badge.length) {
                extraData.badgeLabel = badge[0].innerHTML;
            }

            switch (contentType) {
                case 'shows':
                    id = aalink[9];
                    extraData.notificationId = id;
                    this.initShow(element, id, extraData);
                    break;
                case 'movies':
                    id = aalink[9];
                    extraData.notificationId = aalink[6];
                    this.initMovie(element, id, extraData);
                    break;
                case 'live':
                    id = aalink[26];
                    extraData.notificationId = aalink[9];
                    this.initLive(element, id, extraData);
                    break;
                default:
                    break;
            }
        }
    }

    initShow(element, id, extraData) {
        if (!id) {
            this.__enableHoverEffect(element);
            return;
        }

        const url = `/highlight/shows/${id}/`;
        const href = element.getAttribute('href');
        const button = {
            label: getLocale('watch_now_button'),
            play: true,
            url: href,
        };

        if (extraData.locked) {
            button.label = getLocale('upgrade_cta');
            button.url = extraData.upsellUrl;
        }

        extraData = {
            ...extraData,
            button,
            id,
        };

        if (this.kw[id]) {
            extraData = {
                ...extraData,
                ...this.kw[id],
            };
        }

        this._http
            .doGet(url)
            .then((res) => {
                if (!res.success) {
                    this.__enableHoverEffect(element);
                    return;
                }

                res = res.data;
                res = { ...res.highlight, ...extraData };

                this.cache[id] = res;

                this.__initElement(element, this.cache[id]);
            })
            .catch(() => {
                this.__enableHoverEffect(element);
                return;
            });
    }

    initMovie(element, id, extraData) {
        if (!id) {
            this.__enableHoverEffect(element);
            return;
        }

        const url = `/highlight/movies/${id}/`;
        const href = element.getAttribute('href');
        const button = {
            label: getLocale('watch_now_button'),
            play: true,
            url: href,
        };

        if (extraData.locked) {
            button.label = getLocale('upgrade_cta');
            button.url = extraData.upsellUrl;
        }

        extraData = {
            ...extraData,
            isMovie: true,
            id,
            button,
        };

        if (this.kw[id]) {
            extraData = {
                ...extraData,
                ...this.kw[id],
            };
        }

        this._http
            .doGet(url, {
                notificationId: extraData.notificationId,
            })
            .then((res) => {
                if (!res.success) {
                    this.__enableHoverEffect(element);
                    return;
                }

                res = res.data;
                res = { ...res.highlight, ...extraData };

                this.cache[id] = res;

                this.__initElement(element, this.cache[id]);
            })
            .catch(() => {
                this.__enableHoverEffect(element);
                return;
            });
    }

    initLive(element, id, extraData) {
        if (!id) return;

        const url = `/highlight/live-events/${id}/`;
        const href = element.getAttribute('href');
        const logo = element.querySelector('.channel-logo img')?.src;
        const button = {
            label: getLocale('watch_live'),
            play: true,
            url: href,
        };

        if (extraData.locked) {
            button.label = getLocale('upgrade_cta');
            button.url = extraData.upsellUrl;
        }

        let userState = CBS.Registry.user_auth ? 'rg' : 'an';
        if (userState === 'an' || CBS.Registry.user.sub_status === 'REGISTERED') {
            // if guest user,
            button.label = getLocale('try_it_free'); // "Try it free" for guest users
            button.url = extraData.upsellUrl;
            button.play = false;
        }
        if (CBS.Registry.user.sub_status === 'EX_SUBSCRIBER') {
            button.label = getLocale('get_started'); // "Get started" for ex-subscribers
            button.url = extraData.upsellUrl;
            button.play = false;
        }

        extraData = {
            ...extraData,
            isLive: true,
            logo,
            button,
            id,
        };

        let localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        this._http
            .doGet(url, {
                timeZone: localTimeZone,
                notificationId: extraData.notificationId,
            })
            .then((res) => {
                if (!res.success) {
                    this.__enableHoverEffect(element);
                    return;
                }

                res = res.data;
                res = { ...res.highlight, ...extraData };
                res.allowMyListControls = res.isUpcomingListing ? res.allowMyListControls : false;

                if (res.isUpcomingListing) {
                    if (CBS.Registry.user.sub_status === 'EX_SUBSCRIBER') {
                        res.button.label = getLocale('get_started');
                    } else if (CBS.Registry.user.sub_status !== 'SUBSCRIBER') {
                        res.button.label = getLocale('try_it_free');
                    } else {
                        res.button.label = getLocale('go_to_details');
                    }
                    res.button.play = false;
                }
                this.cache[id] = res;

                this.__initElement(element, this.cache[id]);
            })
            .catch(() => {
                this.__enableHoverEffect(element);
                return;
            });
    }

    __createExpandedElement(element, data) {
        data.contentMyListActive = this.watchlistService.isContentInWatchlist(data.id);
        data.contentNotificationActive = this.notificationService.isContentInNotificationList(
            data.notificationId,
        );
        const expandedElement = super.__createExpandedElement(element, data);
        const initial = element.getBoundingClientRect();
        let pageSection = element.closest('.search__results--display');

        let topCoordinate = initial.offsetTop + pageSection.offsetTop;

        expandedElement.style.top = topCoordinate + 'px';

        return expandedElement;
    }

    __removeExpandedElement(element) {
        if (!this.expandedElement) {
            return;
        }

        const initial = element.getBoundingClientRect();
        let pageSection = element.closest('.search__results--display');

        let topCoordinate = initial.offsetTop + pageSection.offsetTop;

        this.expandedElement.style.top = topCoordinate + 'px';

        super.__removeExpandedElement(element);
    }

    __expandElement(element, expandedElement) {
        // design width spec is 1.5x the 16:9 thumbnail at each breakpoint
        const growthRate = 1.5;
        // 48 is margins around bottom component (24px each side)
        const expandedWidth =
            SearchContentHighlight.thumbnailToWindowRatio.get(this.bp.getCurrentBreakpointName()) *
                window.innerWidth *
                growthRate +
            48;

        expandedElement.style.display = 'block';

        let positionObj = calculatePosition(
            element,
            expandedWidth,
            expandedWidth,
            this.expandedElement,
        );
        Object.assign(expandedElement.style, positionObj);

        expandedElement.style.width = `${expandedWidth}px`;
        expandedElement.style.height = `auto`;
        expandedElement.style.opacity = 1;

        // 48 is padding around bottom components (24 each side)
        expandedElement.querySelector(
            '.ch-expanded-overlay-components',
        ).firstElementChild.style.width = `${expandedWidth - 48}px`;
        // set min height so container fills the space while image loads, inverse aspect ratio * width = height
        expandedElement.querySelector('.media-manager-container').style.minHeight =
            (1 / 1.78) * expandedWidth + 'px';

        trackAction('trackCarouselTileInFocus', this.__getTrackingSpecs(element, expandedElement));

        setTimeout(() => {
            if (this.expandedElement) {
                this.expandedElement.querySelector(
                    '.ch-expanded-overlay-components',
                ).style.opacity = 1;
            }
        }, 200);

        expandedElement.addEventListener('mouseleave', () => {
            this.__removeExpandedElement(element);
        });
    }

    __createMediaManager(data, target) {
        const mediaManagerOptions = {
            target,
            videoLogoUrl: data.logo,
            imageUrl: data.poster,
            fallbackText: data.titleText,
            altImage: data.alt,
            videoContentId: data.videoContentId,
            isLive: data.isLive,
            isUpcoming: data.isUpcomingListing,
            scheduleBadge: data.scheduleBadge,
            scheduleBadgeText: data.scheduleBadgeText,
            isEvent: data.isEvent,
            hlsVideoUrl: data.videoPreviewURL ?? '',
            previewContainer: `search_${data.videoContentId}`,
        };

        this.mediaManager = new MediaManager(mediaManagerOptions);
        this.mediaManager.muted = localStorage.getItem('spliceVolumeOn') !== '1';
    }

    __spreadStateEvent(id, toggleClassName) {
        dispatchEventToAllCarousels(
            new CustomEvent(HighlightCarousel.STATE_CAROUSEL_EVENT_TYPE, {
                detail: {
                    toggleClassName,
                    elementId: id,
                },
            }),
        );

        if (toggleClassName === PreferencesCta.NOTIFY_ON_CLASS) {
            const cta = this.expandedElement?.querySelector('#notify-cta-' + id);
            const _link = cta?.parentElement;
            if (_link) {
                _link.classList.toggle('active');
                _link.classList.toggle('notify_on');
            }

            this.cache[id].contentNotificationActive = !this.cache[id].contentNotificationActive;
        }

        if (toggleClassName === WatchlistCta.IN_WATCHLIST_CLASS) {
            const cta = this.expandedElement?.querySelector('#watchlist-cta-' + id);
            const _link = cta?.parentElement;
            if (_link) {
                _link.classList.toggle('active');
                _link.classList.toggle('in_watchlist');
            }

            this.cache[id].contentMyListActive = !this.cache[id].contentMyListActive;
        }
    }

    __getTrackingEventString() {
        return TRACK_SEARCH_SELECT;
    }

    __getSearchTracking(element) {
        const searchWrapper = document.querySelector('#search');
        const inputBarWrapper = searchWrapper.querySelector('.search__inputBar');
        const input = inputBarWrapper.querySelector('input');

        let parent = element.closest('.search__results--display');
        let prevSibling = parent.previousElementSibling;
        let menuItemLabel = prevSibling.innerText.trim();

        return {
            searchTerm: input?.value ?? '',
            suggestedSearchTerm: $('.subnav__items a.active').text().trim() ?? '',
            menuItemLabel,
        };
    }

    __getTrackingSpecs(element, expandedElement) {
        const aaLink = element.getAttribute('aa-link')?.split('|') ?? [];
        const pageType = 'search';
        const searchEventComplete = 1;
        const screenName = '/search/';
        const showSeriesId = aaLink[5];
        const movieId = aaLink[6];
        const liveTvChannel = aaLink[24];
        const stationCode = aaLink[25];
        const video = this.mediaManager.video ?? null;
        const hasVideo = video ? true : false;
        const contentLocked = aaLink[23];
        const upcomingEvent = aaLink[21] === 'upcoming-event';
        let contentStatus = aaLink[27] ?? 'is not live';
        let showSeriesTitle = '';
        let movieTitle = '';

        if (showSeriesId || liveTvChannel) {
            showSeriesTitle = aaLink[4];
        }
        if (movieId) {
            movieTitle = aaLink[4];
        }
        if (upcomingEvent) {
            showSeriesTitle = aaLink[4];
            contentStatus = 'is not live';
        }

        let obj = {
            pageType,
            screenName,
            showSeriesId,
            showSeriesTitle,
            movieId,
            movieTitle,
            searchEventComplete,
            liveTvChannel,
            stationCode,
            optimizelyExp: CBS.Tracking.utag_data.optimizelyExp,
            contentLocked,
            displayId: this.displayId,
            contentStatus: contentStatus,
            posRowNum: '0',
            posColNum: aaLink[3],
            midSearchSuggestedId: $('.subnav__items a.active').data('gid') ?? '',
        };

        if (hasVideo) {
            const videoPreviewId = this.mediaManager.videoContentId;
            obj = { ...obj, ...{ videoPreviewId: videoPreviewId } };
        }

        obj = { ...obj, ...this.__getSearchTracking(element) };
        obj = this.__getSuggestionsSubNavTracking(obj);

        return obj;
    }

    __getSuggestionsSubNavTracking(obj) {
        obj.searchFilterInView = [];
        obj.searchIdInView = [];
        const searchSuggestionsSubNav = document.querySelector(
            ".presearch-subnav:not([style*='display:none'])",
        );
        if (searchSuggestionsSubNav) {
            const searchSuggestionsSubNavAnchors = searchSuggestionsSubNav.querySelectorAll('li a');
            searchSuggestionsSubNavAnchors.forEach((anchor, index) => {
                const anchorText = anchor.textContent ?? '';
                const anchorId = anchor.dataset.gid ?? '';
                obj.searchFilterInView.push(anchorText);
                obj.searchIdInView.push(anchorId);
            });
        }
        return obj;
    }

    __enableHoverEffect(element) {
        element.classList.remove('ch-search');
    }
}
